import { useEffect, useState } from "react";
import { getActivityUsers } from "../../api/analyticsAdmin";
import {
  buildPersonIndex,
  PersonIndex,
} from "../../helpers/analyticsPlusJourney";
import { AnalyticsFilters } from "./AnalyticsFilterBar";

const INDEX_PAGE_SIZE = 100;
const MAX_INDEX_PAGES = 50;

export interface PersonIndexState {
  index: PersonIndex;
  building: boolean;
  truncated: boolean;
}

export const usePersonIndex = (filters: AnalyticsFilters): PersonIndexState => {
  const [state, setState] = useState<PersonIndexState>({
    index: new Map(),
    building: true,
    truncated: false,
  });

  useEffect(() => {
    let cancelled = false;
    const build = async () => {
      setState({ index: new Map(), building: true, truncated: false });
      const rows: { distinctId: string; personId: string | null }[] = [];
      let pageNumber = 1;
      let truncated = false;
      try {
        // Loop pages until the list is exhausted or the page cap is hit.
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const response = await getActivityUsers({
            from: filters.from,
            to: filters.to,
            platform: filters.platform,
            audience: filters.audience,
            pagination: { pageNumber, pageSize: INDEX_PAGE_SIZE },
          });
          if (cancelled) return;
          response.data.forEach((row) =>
            rows.push({ distinctId: row.distinctId, personId: row.personId })
          );
          const fetched = pageNumber * INDEX_PAGE_SIZE;
          if (fetched >= response.total) break;
          if (pageNumber >= MAX_INDEX_PAGES) {
            truncated = true;
            break;
          }
          pageNumber += 1;
        }
        if (!cancelled) {
          setState({ index: buildPersonIndex(rows), building: false, truncated });
        }
      } catch {
        // On failure, fall back to an empty index (modal degrades to single id).
        if (!cancelled) {
          setState({ index: new Map(), building: false, truncated: false });
        }
      }
    };
    build();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  return state;
};
